/**
 * Parse distance from various text formats
 * Handles:
 * - Single numbers: "160", "160 km"
 * - Ranges: "160-180 km", "160–180 km" (en dash)
 * - With ± symbol: "± 160–180 km", "±160-180 km"
 * - With "Distance:" prefix: "Distance: ± 160–180 km"
 * 
 * @param {string|number} input - Distance input (string or number)
 * @returns {Object} Parsed distance with km, min, max, and originalText
 */
export function parseDistance(input) {
  if (!input) {
    return { km: null, min: null, max: null, originalText: null, isRange: false };
  }

  // If already a number, return as-is
  if (typeof input === 'number') {
    return {
      km: input,
      min: input,
      max: input,
      originalText: String(input),
      isRange: false,
    };
  }

  const originalText = String(input).trim();
  let text = originalText;

  // Remove common prefixes
  text = text.replace(/^distance:\s*/i, '');
  text = text.replace(/^dist:\s*/i, '');
  
  // Remove ± symbol and surrounding spaces
  text = text.replace(/±\s*/g, '');
  
  // Remove "km" suffix (case insensitive)
  text = text.replace(/\s*km\s*$/i, '');
  
  // Remove any other non-numeric characters except dash, en-dash, em-dash, and spaces
  text = text.replace(/[^\d\s\-\u2013\u2014]/g, '');
  text = text.trim();

  // Try to match range patterns: "160-180", "160 – 180", "160—180"
  const rangePattern = /(\d+(?:\.\d+)?)\s*[\-\u2013\u2014]\s*(\d+(?:\.\d+)?)/;
  const rangeMatch = text.match(rangePattern);

  if (rangeMatch) {
    const min = parseFloat(rangeMatch[1]);
    const max = parseFloat(rangeMatch[2]);
    
    if (!isNaN(min) && !isNaN(max) && min <= max) {
      // Use midpoint for the main km value (or average)
      const avg = (min + max) / 2;
      return {
        km: Math.round(avg * 100) / 100, // Round to 2 decimal places
        min: min,
        max: max,
        originalText: originalText,
        isRange: true,
      };
    }
  }

  // Try to match single number
  const singleNumberPattern = /(\d+(?:\.\d+)?)/;
  const singleMatch = text.match(singleNumberPattern);
  
  if (singleMatch) {
    const value = parseFloat(singleMatch[1]);
    if (!isNaN(value)) {
      return {
        km: value,
        min: value,
        max: value,
        originalText: originalText,
        isRange: false,
      };
    }
  }

  // If parsing fails, return null
  return { km: null, min: null, max: null, originalText: originalText, isRange: false };
}

/**
 * Format distance for display
 * @param {Object} distance - Distance object with km, min, max, isRange
 * @returns {string} Formatted distance string
 */
export function formatDistance(distance) {
  if (!distance || distance.km === null) {
    return 'N/A';
  }

  if (distance.isRange && distance.min !== distance.max) {
    return `${distance.min}–${distance.max} km`;
  }

  return `${distance.km} km`;
}

